<script setup lang="ts">
import { CourseContentActivityType, CourseSubViewType } from "~/enums/course.enums";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { LearningActivity } from "~/composables/useCourse";

const {
  course,
  subView,
  getPreviousActivity,
  getNextActivity,
} = useCourseState();
const {
  quiz,
  currentAttempt,
  currentQuestion,
  remainingQuestionsCount,
} = useQuizStore();
const route = useRoute();
const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps<{
  currentActivityId?: string;
}>();

const prevActivity = computed(() => {
  if (!props.currentActivityId) {return;}
  return getPreviousActivity(props.currentActivityId);
});

const nextActivity = computed(() => {
  if (!props.currentActivityId) {return;}
  return getNextActivity(props.currentActivityId);
});

const getActivityLink = (activity?: LearningActivity) => {
  const courseBase = `/courses/${route.params.courseId as string}`;

  if (!activity) {
    return localePath(courseBase);
  }

  if (activity.type === CourseContentActivityType.Lesson) {
    return localePath(`${courseBase}/lessons/${activity.id}`);
  }
  if ([CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(activity.type)) {
    return localePath(`${courseBase}/quizzes/${activity.id}`);
  }
  if (activity.type === CourseContentActivityType.Case) {
    return localePath(`${courseBase}/cases/${activity.id}`);
  }

  return localePath(courseBase);
};

const progressBarValue = computed(() => {
  const { questions } = quiz.value || {};
  const totalQuestions = questions?.length || 0;

  if (totalQuestions === 0) {
    return 0;
  }

  const isQuestionCompleted = (currentQuestion.value?.isRevealingAnswer ? 1 : 0);
  const answeredCount = totalQuestions - remainingQuestionsCount.value + isQuestionCompleted;
  return Math.min(100, (answeredCount / totalQuestions) * 100);
});

const currentQuestionCount = computed(() => {
  const totalQuestions = quiz.value?.questions?.length || 0;
  const answeredCount = totalQuestions - remainingQuestionsCount.value;

  if (totalQuestions === 0) {
    return '0/0';
  }

  const currentCount = Math.min(answeredCount + 1, totalQuestions);
  return `${currentCount}/${totalQuestions}`;
});

const showNavigationButtons = computed(() => {
  return [CourseSubViewType.Lesson, CourseSubViewType.Comparison].includes(subView.value as CourseSubViewType);
});

const showProgressBar = computed(() => {
  return subView.value === CourseSubViewType.Quiz
      && currentAttempt.value
      && currentAttempt.value.isStarted
      && !currentAttempt.value.isFinished
      && quiz.value;
});
</script>

<template>
  <div
    :class="[
      'sticky top-0 bg-white z-[900] flex flex-col',
      'md:h-[72px] md:min-h-[72px] md:max-h-[72px]',
      'h-[50px] min-h-[50px] max-h-[50px]',
      'md-h:h-[50px] md-h:min-h-[50px] md-h:max-h-[50px]'
    ]"
  >
    <div class="flex items-center justify-between h-full">
      <div class="flex items-center h-full gap-4 md:pl-4">
        <NuxtLink :to="localePath(`/courses/${course?.id}`)">
          <Button
            link
            class="flex items-center gap-3 pr-6 pl-4"
          >
            <FontAwesomeIcon icon="fa-chevron-left" />
            <span class="text-14 font-bold">{{t('global.button.back')}}</span>
          </Button>
        </NuxtLink>

        <Divider
          layout="vertical"
          class="m-0 xl:block hidden"
        />
      </div>

      <div class="flex items-center h-full">
        <template v-if="showNavigationButtons">
          <Divider
            layout="vertical"
            class="m-0"
          />

          <div class="flex items-center p-4">
            <NuxtLink
              v-if="prevActivity"
              :to="getActivityLink(prevActivity)"
            >
              <Button
                text
                severity="secondary"
                class="md:p-4 p-2"
              >
                <FontAwesomeIcon
                  icon="fa-backward"
                  class="md:text-16 text-14 text-neutral-90"
                />
              </Button>
            </NuxtLink>

            <NuxtLink
              v-if="nextActivity"
              :to="getActivityLink(nextActivity)"
            >
              <Button
                text
                severity="secondary"
                class="md:p-4 p-2"
              >
                <FontAwesomeIcon
                  icon="fa-forward"
                  class="md:text-16 text-14 text-neutral-90"
                />
              </Button>
            </NuxtLink>
          </div>
        </template>

        <template v-if="showProgressBar">
          <Divider
            layout="vertical"
            class="m-0 xl:block hidden"
          />

          <div class="flex items-center gap-4 sm:w-[300px] w-[220px] md:px-6 px-4">
            <div class="text-14 font-bold text-nowrap tracking-widest text-neutral-60">
              {{t('course.toolbar.quiz.question')}} {{currentQuestionCount}}
            </div>

            <ProgressBar
              :value="progressBarValue"
              class="w-full h-3 rounded-full bg-neutral-30"
              :pt="{
                value: {
                  class: 'bg-blue-50 rounded-full'
                }
              }"
              :show-value="false"
            />
          </div>
        </template>
      </div>
    </div>

    <Divider class="m-0" />
  </div>
</template>